import React, { useState, useEffect } from 'react';
import { FaComment, FaPaperPlane, FaReply, FaTrash, FaEdit } from 'react-icons/fa';
import axios from 'axios';
import '../styles/CommentSection.css';

const API = process.env.REACT_APP_API_URL || '';

function CommentSection({ type, id, currentUser }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    if (id) loadComments();
  }, [type, id]);

  const loadComments = async () => {
    try {
      const res = await axios.get(`${API}/api/comments/${type}/${id}`);
      setComments(res.data);
    } catch (e) { /* ignore */ }
  };

  const submit = async () => {
    if (!newComment.trim()) return;
    try {
      const body = { content: newComment };
      if (type === 'album') body.album_id = id;
      else body.track_id = id;
      if (replyTo) body.parent_id = replyTo;

      await axios.post(`${API}/api/comments`, body);
      setNewComment('');
      setReplyTo(null);
      loadComments();
    } catch (e) { console.error(e); }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    try {
      await axios.delete(`${API}/api/comments/${cid}`);
      loadComments();
    } catch (e) { console.error(e); }
  };

  const saveEdit = async (cid) => {
    if (!editContent.trim()) return;
    try {
      await axios.put(`${API}/api/comments/${cid}`, { content: editContent });
      setEditingId(null);
      loadComments();
    } catch (e) { console.error(e); }
  };

  const formatDate = (d) => {
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `Il y a ${Math.floor(diff/60000)}min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff/3600000)}h`;
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const parentComments = comments.filter(c => !c.parent_id);
  const replies = (parentId) => comments.filter(c => c.parent_id === parentId);

  return (
    <div className="comment-section">
      <h3><FaComment /> Commentaires ({comments.length})</h3>

      <div className="comment-input-area">
        {replyTo && (
          <div className="replying-to">
            Réponse à {comments.find(c => c.id === replyTo)?.username}
            <button onClick={() => setReplyTo(null)}>✕</button>
          </div>
        )}
        <div className="comment-input-row">
          <input
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder="Ajouter un commentaire..."
            onKeyDown={e => e.key === 'Enter' && submit()}
          />
          <button onClick={submit} disabled={!newComment.trim()}><FaPaperPlane /></button>
        </div>
      </div>

      <div className="comments-list">
        {parentComments.map(c => (
          <div key={c.id} className="comment-item">
            <div className="comment-header">
              <span className="comment-author">{c.display_name || c.username}</span>
              <span className="comment-date">{formatDate(c.created_at)}</span>
            </div>
            {editingId === c.id ? (
              <div className="comment-edit">
                <input value={editContent} onChange={e => setEditContent(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveEdit(c.id)} />
                <button onClick={() => saveEdit(c.id)}>✓</button>
                <button onClick={() => setEditingId(null)}>✕</button>
              </div>
            ) : (
              <p className="comment-content">{c.content}</p>
            )}
            <div className="comment-actions">
              <button onClick={() => { setReplyTo(c.id); }}><FaReply /> Répondre</button>
              {currentUser && (currentUser.id === c.user_id || currentUser.role === 'admin') && (
                <>
                  <button onClick={() => { setEditingId(c.id); setEditContent(c.content); }}><FaEdit /></button>
                  <button onClick={() => deleteComment(c.id)}><FaTrash /></button>
                </>
              )}
            </div>
            {/* Réponses */}
            {replies(c.id).map(r => (
              <div key={r.id} className="comment-reply">
                <div className="comment-header">
                  <span className="comment-author">{r.display_name || r.username}</span>
                  <span className="comment-date">{formatDate(r.created_at)}</span>
                </div>
                <p className="comment-content">{r.content}</p>
                {currentUser && (currentUser.id === r.user_id || currentUser.role === 'admin') && (
                  <div className="comment-actions">
                    <button onClick={() => deleteComment(r.id)}><FaTrash /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
        {comments.length === 0 && <div className="no-comments">Aucun commentaire</div>}
      </div>
    </div>
  );
}

export default CommentSection;
